import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import TextField from "../../Components/TextField";
import PhoneVerify from "../../Components/PhoneVerify";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import {
  getTeamInvitationByToken,
  acceptTeamInvitation,
  checkUserExists,
  sendEmailOtp,
  verifyEmailOtp,
} from "../../Utils/apiServices";
import { stripNumbersFromName } from "../../Utils/nameValidation";

const COUNTRY_CODE_TO_PREFIX = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };

const PASSWORD_REQUIREMENT_MSG =
  "Password must be at least 8 characters and include uppercase, lowercase, number, and a special character (e.g. #)";

const getEmptyForm = () => ({
  first_name: "",
  last_name: "",
  username: "",
  password: "",
  re_enter_password: "",
  phone_number: "",
  country_code: "US",
});

// Defined at module scope (NOT inside the component) so it keeps a stable
// component identity across renders — otherwise React remounts the whole
// subtree every render, which loops PhoneVerify's mount effect forever.
const Shell = ({ children }) => (
  <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 py-10">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
      {children}
    </div>
  </div>
);

// Public page reached from a co-founder invitation email. The invitee sets up
// their own student account; on submit they're linked to the idea as a
// SECONDARY_STUDENT and the invitation is marked accepted.
const AcceptTeamInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [formData, setFormData] = useState(getEmptyForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [usernameExistsError, setUsernameExistsError] = useState("");
  const latestUsernameRef = useRef("");
  // Email ownership verification (OTP) — must pass before joining.
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  // Phone ownership verification (Twilio Verify SMS OTP) — must pass before joining.
  // The verified number is kept (rather than a bare boolean) so the flag survives a
  // PhoneVerify remount and self-invalidates the moment the number is edited.
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState("");
  const [currentPhoneE164, setCurrentPhoneE164] = useState("");
  const phoneVerified = Boolean(currentPhoneE164) && currentPhoneE164 === verifiedPhoneE164;

  const handlePhoneVerifiedChange = (isVerified, e164) => {
    setCurrentPhoneE164(e164 || "");
    if (isVerified) setVerifiedPhoneE164(e164 || "");
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        setLoadError("This invitation link is missing its token.");
        setLoading(false);
        return;
      }
      try {
        const res = await getTeamInvitationByToken(token);
        if (!active) return;
        if (res?.success) {
          const data = res.data?.data ?? res.data;
          setInvite(data);
          setFormData((prev) => ({
            ...prev,
            first_name: data?.first_name || "",
            last_name: data?.last_name || "",
          }));
        } else {
          setLoadError(res?.error || "This invitation link is invalid or has expired.");
        }
      } catch (err) {
        if (active) setLoadError(err?.error || "This invitation link is invalid or has expired.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const handleInputChange = (field, value) => {
    if (field === "first_name" || field === "last_name") {
      value = stripNumbersFromName(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
    if (field === "username") {
      latestUsernameRef.current = value;
      setUsernameExistsError("");
    }
  };

  const handleUsernameBlur = () => {
    const valueToCheck = (formData.username?.trim?.() || "").toLowerCase();
    if (!valueToCheck) {
      setUsernameExistsError("");
      return;
    }
    latestUsernameRef.current = valueToCheck;
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestUsernameRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const field = res?.data?.data?.field;
        if (exists && field === "username") {
          setUsernameExistsError("This username is already taken.");
        } else {
          setUsernameExistsError("");
        }
      })
      .catch(() => {
        if (latestUsernameRef.current === valueToCheck) setUsernameExistsError("");
      });
  };

  const handleSendOtp = async () => {
    if (!invite?.email) return;
    setSendingOtp(true);
    setOtpError("");
    setOtpInfo("");
    try {
      const res = await sendEmailOtp({ email: invite.email });
      const sent = res?.success && res?.data?.success !== false;
      if (sent) {
        setOtpSent(true);
        setOtpInfo(`We sent a 6-digit code to ${invite.email}.`);
      } else {
        setOtpError(res?.data?.message || res?.error || "Unable to send the verification code. Please try again.");
      }
    } catch (err) {
      setOtpError(err?.error || "Unable to send the verification code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = (otpCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setOtpError("Please enter the 6-digit verification code.");
      return;
    }
    setVerifyingOtp(true);
    setOtpError("");
    try {
      const res = await verifyEmailOtp({ email: invite.email, otp_code: code });
      const verified = res?.success && res?.data?.success === true;
      if (verified) {
        setEmailVerified(true);
        setOtpInfo("");
      } else {
        setOtpError(res?.data?.message || res?.error || "Verification failed. Please check the code and try again.");
      }
    } catch (err) {
      setOtpError(err?.error || "Verification failed. Please try again.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const validate = () => {
    const username = (formData.username?.trim?.() || "").toLowerCase();

    if (!emailVerified) {
      setError("Please verify your email before joining.");
      return false;
    }
    if (!phoneVerified) {
      setError('Please verify your phone number — use "Verify with OTP" under the phone field.');
      return false;
    }
    if (!formData.first_name || !formData.last_name) {
      setError("First name and last name are required");
      return false;
    }
    if (!username) {
      setError("Username is required");
      return false;
    }
    const usernameRegex = /^(?=.{3,30}$)[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;
    if (!usernameRegex.test(username)) {
      setError(
        "Username must be 3–30 characters using letters, numbers, '.', '_', or '-', and start/end with a letter or number"
      );
      return false;
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])\S{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError(PASSWORD_REQUIREMENT_MSG);
      return false;
    }
    if (formData.password !== formData.re_enter_password) {
      setError("Passwords do not match");
      return false;
    }
    if (!formData.phone_number) {
      setError("Phone number is required");
      return false;
    }
    const phoneRegex = /^\d{10,15}$/;
    if (!phoneRegex.test(formData.phone_number.replace(/\s+/g, ""))) {
      setError("Phone number must be 10-15 digits");
      return false;
    }
    if (usernameExistsError) {
      setError(usernameExistsError);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    const prefix = COUNTRY_CODE_TO_PREFIX[formData.country_code || "US"] || "+1";
    const phoneDigits = (formData.phone_number || "").replace(/\D/g, "");

    setSubmitting(true);
    try {
      const res = await acceptTeamInvitation({
        token,
        username: formData.username.trim().toLowerCase(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        // Prefer the number that was actually OTP-verified — COUNTRY_CODE_TO_PREFIX
        // only covers a handful of countries and falls back to "+1", which would
        // store a different number than the one we verified.
        phone: currentPhoneE164 || prefix + phoneDigits,
      });
      if (res?.success) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2500);
      } else {
        setError(res?.error || "Could not complete your signup. Please try again.");
      }
    } catch (err) {
      setError(err?.error || "An error occurred while completing your signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
          Loading your invitation…
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center py-6">
          <Icon icon="lucide:link-2-off" className="h-10 w-10 text-red-400" />
          <Typography as="h1" className="text-lg font-semibold text-primary-900">
            Invitation unavailable
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            {loadError}
          </Typography>
          <Link to="/login" className="text-sm font-medium text-violet-600 hover:underline mt-2">
            Go to login
          </Link>
        </div>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center py-6">
          <Icon icon="lucide:check-circle" className="h-10 w-10 text-green-500" />
          <Typography as="h1" className="text-lg font-semibold text-primary-900">
            You're on the team!
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            Your account is set up and you've been added to
            {invite?.idea?.title ? ` "${invite.idea.title}"` : " the idea"} as a team member.
            Redirecting you to log in…
          </Typography>
          <Link to="/login" className="text-sm font-medium text-violet-600 hover:underline mt-2">
            Go to login now
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-col gap-1 mb-6 text-center">
        <Typography as="h1" className="text-xl font-bold text-primary-900">
          Join the team
        </Typography>
        <Typography as="p" className="text-sm text-tertiary-600">
          {invite?.inviter?.name ? `${invite.inviter.name} invited you` : "You've been invited"} to
          collaborate on
          {invite?.idea?.title ? ` "${invite.idea.title}"` : " an idea"}. Set up your account to
          join with view-only access.
        </Typography>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-left">
        <TextField
          label="Email"
          name="invite_email"
          type="email"
          value={invite?.email || ""}
          disabled
          readOnly
          helperText="The invitation was sent to this address."
        />

        {emailVerified ? (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <Icon icon="lucide:check-circle" className="h-4 w-4" />
            Email verified
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <Typography as="p" className="text-sm text-slate-700">
              Verify your email to continue.
            </Typography>
            {!otpSent ? (
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleSendOtp}
                disabled={sendingOtp}
                className="self-start bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                startIcon={<Icon icon="lucide:mail" className="h-4 w-4" />}
              >
                {sendingOtp ? "Sending..." : "Send verification code"}
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => {
                      setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      if (otpError) setOtpError("");
                    }}
                    placeholder="123456"
                    className="w-32 rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-lg tracking-[0.4em] font-semibold text-slate-900 placeholder:tracking-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || otpCode.length !== 6}
                  >
                    {verifyingOtp ? "Verifying..." : "Verify"}
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp}
                  className="self-start text-xs font-medium text-violet-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                >
                  {sendingOtp ? "Sending..." : "Resend code"}
                </button>
              </div>
            )}
            {otpInfo && !otpError && (
              <Typography as="p" className="text-xs text-slate-600">
                {otpInfo}
              </Typography>
            )}
            {otpError && (
              <Typography as="p" className="text-xs text-red-600">
                {otpError}
              </Typography>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="First Name"
            name="invite_first_name"
            required
            value={formData.first_name}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            placeholder="First name"
          />
          <TextField
            label="Last Name"
            name="invite_last_name"
            required
            value={formData.last_name}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            placeholder="Last name"
          />
        </div>
        <TextField
          label="Username"
          name="invite_username"
          required
          value={formData.username}
          onChange={(e) => handleInputChange("username", e.target.value)}
          onBlur={handleUsernameBlur}
          placeholder="Choose a username"
          error={Boolean(usernameExistsError)}
          helperText={usernameExistsError || "3–30 characters. Use letters, numbers, '.', '_', or '-'. No spaces."}
        />
        <TextField
          label="Password"
          name="invite_password"
          type="password"
          required
          showPasswordToggle
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          placeholder="Create a password"
          helperText="At least 8 characters with uppercase, lowercase, number, and a special character (e.g. #). No spaces."
        />
        <TextField
          label="Re-enter Password"
          name="invite_re_enter_password"
          type="password"
          required
          showPasswordToggle
          value={formData.re_enter_password}
          onChange={(e) => handleInputChange("re_enter_password", e.target.value)}
          placeholder="Enter the password again"
          error={Boolean(
            formData.re_enter_password && formData.re_enter_password !== formData.password
          )}
          helperText={
            formData.re_enter_password && formData.re_enter_password !== formData.password
              ? "Passwords do not match"
              : undefined
          }
        />
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700 text-left">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <PhoneVerify
            countryCode={formData.country_code}
            phone={formData.phone_number}
            onPhoneChange={(countryCode, phone) => {
              setFormData((prev) => ({ ...prev, country_code: countryCode, phone_number: phone }));
              setError("");
            }}
            onVerifiedChange={handlePhoneVerifiedChange}
            verifiedE164={verifiedPhoneE164}
            placeholder="Enter your phone number"
          />
          {!phoneVerified && (
            <p className="text-xs text-amber-600 text-left">
              Verify this number with the code we text you — required to join.
            </p>
          )}
        </div>

        {error && (
          <Typography as="p" className="text-sm text-red-600">
            {error}
          </Typography>
        )}

        <Button
          variant="primary"
          type="submit"
          disabled={submitting || !emailVerified || !phoneVerified}
          fullWidth
        >
          {submitting ? "Setting up your account…" : "Join the team"}
        </Button>
        <Typography as="p" className="text-xs text-tertiary-500 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-violet-600 hover:underline">
            Log in
          </Link>
        </Typography>
      </form>
    </Shell>
  );
};

export default AcceptTeamInvite;
